import moment from "moment"
import browserID from "browser-id"
import currencyFormatter from "currency.js"
import store from "../store/store"
import { notify as vueNotify } from "@kyvg/vue3-notification"
import i18n from "../i18n"
import { DOCUMENT_VERSIONING_TYPES, FOREIGNER, STATUS, TRANSACTION_TYPE, TRANSACTION_RETURN_SUB_TYPES, TRANSACTION_SUB_TYPE_PAYMENT_EXPIRED_RETURN, TRANSACTION_DISPLAY_TYPE_SYSTEM_ADD_BALANCE_RETURN, STORAGE_KEYS, STORAGE_ACTION_KEYS } from "@/constants/constants"

const base_url = process.env.VUE_APP_IMG_HOST;

const notify = ({ text = "", title = "", type = "success" }) => {
  if (!["warn", "error", "success"].includes(type)) {
    type = "success";
  }

  vueNotify({
    text,
    title,
    type,
    duration: 4000,
  });
};
const getBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const urlImage = (image, thumbnail = false) => {
  if (thumbnail) {
    if (image && image.thumbnail) {
      if (image.thumbnail.startsWith('http')) {
        return image.thumbnail
      }
      return base_url + "/" + image.thumbnail;
    }
  } else {
    if (image && image.image) {
      if (image.image.startsWith('http')) {
        return image.image
      }
      return base_url + "/" + image.image;
    }
  }
  return null;
};

const getRandomFloat = (min, max, decimals) => {
  const str = (Math.random() * (max - min) + min).toFixed(decimals);

  return parseFloat(str);
};

const helpers = {
  notify,
  getBase64,
  urlImage,
  getRandomFloat,
};

const formattedDuration = (durationMilliseconds) => {
  const duration = moment.duration(durationMilliseconds, "milliseconds");
  const h =
    duration.hours() >= 10 ? `${duration.hours()}` : `0${duration.hours()}`;
  const m =
    duration.minutes() >= 10
      ? `${duration.minutes()}`
      : `0${duration.minutes()}`;
  const s =
    duration.seconds() >= 10
      ? `${duration.seconds()}`
      : `0${duration.seconds()}`;
  return `${h}:${m}:${s}`;
};

function numberWithCommas(x, separator = ',') {
  if (x || x == 0) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  }
  return "";
}

function imageSizeFromBase64(base64) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64;
    img.onload = function () {
      const width = img.width;
      const height = img.height;
      resolve({ width, height });
    };
    img.onerror = function (e) {
      console.error(e);
    };
  });
}

function resizeBase64Img(base64, quantity = 0.8) {
  return new Promise((resolve, reject) => {
    var canvas = document.createElement("canvas");
    var ctx = canvas.getContext("2d");
    var img = new Image();
    img.onload = function () {
      var iw = img.width;
      var ih = img.height;
      var maxW = iw / 2;
      var maxH = ih / 2;
      var scale = Math.min(maxW / iw, maxH / ih);
      var iwScaled = iw * scale;
      var ihScaled = ih * scale;
      canvas.width = iwScaled;
      canvas.height = ihScaled;
      ctx.drawImage(img, 0, 0, iwScaled, ihScaled);
      resolve(canvas.toDataURL("image/jpeg", quantity));
    };
    img.src = base64;
  });
}

function getDeviceUUID() {
  return browserID();
}
function exchange (input, num = 100, asterisk = false, currency = null, ceil = false, currentRates = null) {
  const defaultCurrency = "IDR"
  const user = store.state.userProfile
  const locale = i18n.global.locale.value
  const rates = currentRates || store.state.exchangeRates

  // For IDR
  const separator = locale === 'id' ? '.' : ','
  const decimal = locale === 'id' ? ',' : '.'

  if (!currency) {
    if (user && user.preferred_currency) {
      currency = user.preferred_currency
    } else if (locale && locale === FOREIGNER.LOCALE) {
      currency = FOREIGNER.CURRENCY
    } else {
      currency = defaultCurrency
    }
  }
  if (!input) {
    return `${currency} 0${asterisk && currency === defaultCurrency ? "*" : ""}`
  }
  if (!rates) {
    currency = defaultCurrency
  }
  const rate = rates ? rates[currency] : null
  if (currency === defaultCurrency || !rate) {
    const precision = input % 1 === 0 ? 0: 2
    return currencyFormatter(parseFloat(input), { symbol: `${defaultCurrency} `, separator, decimal, precision }).format()
  }
  const value = rate * input
  const rounded = ceil ? Math.ceil(value * num) / num : Math.floor(value * num) / num
  if (rounded === 0) {
    return exchange(input, num * 10, asterisk, currency)
  }
  return `${currency} ${rounded > 1 ? currencyFormatter(rounded, { symbol: '', separator: ',', decimal: '.' }).format() : rounded}${asterisk ? "*" : ""}`
}

function exchangeNumericValue(idrAmount, currency = null, currentRates = null) {
  if (!idrAmount) return idrAmount
  const user = store.state.userProfile
  const rates = currentRates || store.state.exchangeRates
  const resolvedCurrency = currency || user?.preferred_currency || 'IDR'
  if (resolvedCurrency === 'IDR' || !rates) return idrAmount
  const rate = rates[resolvedCurrency]
  if (!rate) return idrAmount
  return Math.floor(idrAmount * rate * 100) / 100
}

/**
 * Resolve a coherent { value, currency } pair for Meta value-based events from an IDR
 * base amount. Only emits a foreign currency when the amount could actually be converted
 * (a rate exists); otherwise reports the true IDR amount. Mirrors the backend so the
 * browser never sends an un-converted IDR amount mislabeled as a foreign currency, which
 * would inflate ROAS. The final currency string is normalized by metaPixelTrackEvent.
 */
function resolveMetaValueCurrency(idrAmount, preferredCurrency = null) {
  const currency = preferredCurrency || store.state.userProfile?.preferred_currency || 'IDR'
  if (currency === 'IDR') return { value: idrAmount, currency: 'IDR' }
  const converted = exchangeNumericValue(idrAmount, currency)
  // exchangeNumericValue returns the input unchanged when no usable rate is available;
  // if nothing was converted, report the true IDR amount instead of mislabeling it.
  if (converted === idrAmount) return { value: idrAmount, currency: 'IDR' }
  return { value: converted, currency }
}

function getErrorMessage(err) {
  let mess = ""
  if (err) {
    if (err.data) {
      if (typeof err.data === "object") {
        let i = 0
        for (const p in err.data) {
          if (err.data.hasOwnProperty(p)) {
            mess += `${err.data[p]} <br>`
            i++
          }
          if (i === 3) {
            break
          }
        }
      } else {
        mess = err.data
      }
    } else if (err.message) {
      mess = err.message
    } else if (err.error) {
      mess = err.error
    }
  }
  return mess
}

function formatDate(dateStr) {
  const locale = i18n.global.locale.value || 'id'
  const format = locale === 'id' ? 'DD MMM YYYY' : 'MMMM D, YYYY'
  return moment(dateStr).locale(locale).format(format)
}

function formatNumberIntl(value, minimumFractionDigits = 0, maximumFractionDigits = 0) {
  if (value === null || value === undefined) {
    return value;
  }
  const parsedValue = typeof value === 'string' ? Number(value.trim()) : value;
  if (typeof parsedValue !== 'number' || isNaN(parsedValue)) {
    return value;
  }

  const locale = i18n.global.locale.value || 'id';
  return new Intl.NumberFormat(locale, { minimumFractionDigits: minimumFractionDigits, maximumFractionDigits: maximumFractionDigits }).format(parsedValue);
}

function formatPercentage(value) {
  const locale = i18n.global.locale.value
  if (locale === 'id') {
    return value.toString().replace('.', ',');
  }
  return value.toString();
}

function userHasPermission(permission) {
  const userPermissions = store.getters.adminPermissions;
  if (!userPermissions) {
    return false;
  }
  return userPermissions.includes(permission);
}

function isEmail(email) {
  const reg = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/
  return reg.test(email)
}

function appendTimestampToUrl (url) {
  const timestamp = Math.floor(new Date().getTime() / 1000)
  if (url.includes("?")) {
    return `${url}&t=${timestamp}`
  } else {
    return `${url}?t=${timestamp}`
  }
}

function getCookie (name) {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(";").shift()
  return null
}

function getTinyMCEApiKey() {
  return process.env.VUE_APP_TINYMCE_API_KEY || ""
}

// Locale key for a transaction's type label. SYSTEM_ADD_BALANCE return sub_types map to a display-only key.
function getTransactionTypeKey(transaction) {
  if (
    transaction.type === TRANSACTION_TYPE.SYSTEM_ADD_BALANCE
    && TRANSACTION_RETURN_SUB_TYPES.includes(transaction.sub_type)
  ) {
    return TRANSACTION_DISPLAY_TYPE_SYSTEM_ADD_BALANCE_RETURN
  }
  return transaction.type
}

// FK on a sub-typed system credit/debit row → the type of the transaction it belongs to.
// Same order as the backend's TransactionService::attachReturnReferences.
const SYSTEM_BALANCE_SOURCE_TYPES = [
  ['payment_id', TRANSACTION_TYPE.BUY_TOKEN],
  ['order_id', TRANSACTION_TYPE.ORDER_TOKEN],
  ['withdrawal_request_id', TRANSACTION_TYPE.WITHDRAWAL],
]

// Locale keys for a SYSTEM_ADD_BALANCE row's wording, by its sub_type: the title reason
// (TRANSACTION_TYPE_REASONS.*), the row's own "… for BT-…" label, and the "… ID" label its
// source row shows (TRANSACTION.*). One place so title, sub row and ID label always match.
function getCreditLabelKeys(subType) {
  if (subType === TRANSACTION_SUB_TYPE_PAYMENT_EXPIRED_RETURN) {
    return { reason: 'EXPIRY_REFUND', forKey: 'EXPIRY_REFUND_FOR', idKey: 'EXPIRY_REFUND_ID' }
  }
  if (TRANSACTION_RETURN_SUB_TYPES.includes(subType)) {
    return { reason: 'REFUND', forKey: 'REFUND_FOR', idKey: 'REFUNDED_AS' }
  }
  return { reason: 'CREDITED', forKey: 'CREDITED_FOR', idKey: 'CREDITED_AS' }
}

// TRANSACTION.* key for a system row's "… for <source>" label (list sub row and detail).
function getSourceReferenceLabelKey(transaction) {
  if (transaction?.type === TRANSACTION_TYPE.SYSTEM_DEDUCT_BALANCE) return 'LATE_PAYMENT_FOR'
  return getCreditLabelKeys(transaction?.sub_type).forKey
}

function getSystemBalanceReasonKey(transaction) {
  if (transaction.type === TRANSACTION_TYPE.SYSTEM_DEDUCT_BALANCE) return 'LATE_PAYMENT'
  return getCreditLabelKeys(transaction.sub_type).reason
}

// Display label for a transaction's type. Sub-typed system rows name what they belong to,
// e.g. "Buy token (Refund)"; legacy rows without a sub_type keep the plain type label.
function getTransactionTypeLabel(transaction) {
  const t = i18n.global.t
  const isSystemBalance = transaction.type === TRANSACTION_TYPE.SYSTEM_ADD_BALANCE
    || transaction.type === TRANSACTION_TYPE.SYSTEM_DEDUCT_BALANCE
  const source = isSystemBalance && transaction.sub_type
    ? SYSTEM_BALANCE_SOURCE_TYPES.find(([fk]) => transaction[fk])
    : null
  if (!source) {
    return t(`TRANSACTIONS_TYPES.${getTransactionTypeKey(transaction)}`)
  }
  return t('TRANSACTION.TYPE_WITH_REASON', {
    type: t(`TRANSACTIONS_TYPES.${source[1]}`),
    reason: t(`TRANSACTION_TYPE_REASONS.${getSystemBalanceReasonKey(transaction)}`),
  })
}

function getTransactionStatus(transaction) {
  switch (transaction.type) {
    case TRANSACTION_TYPE.ADMIN_ADD_BALANCE:
    case TRANSACTION_TYPE.ADMIN_DEDUCT_BALANCE:
    case TRANSACTION_TYPE.SYSTEM_ADD_BALANCE:
    case TRANSACTION_TYPE.SYSTEM_DEDUCT_BALANCE:
    case TRANSACTION_TYPE.DONATION_RENTAL_INCOME:
    case TRANSACTION_TYPE.ADMIN_DONATION_DISTRIBUTION:
      return STATUS.COMPLETED
    case TRANSACTION_TYPE.BUY_TOKEN:
      if (transaction.payment) {
        return transaction.payment.status
      }
      if (transaction.swap_token_transaction) {
        return transaction.swap_token_transaction.status
      }
      return STATUS.PENDING
    case TRANSACTION_TYPE.ORDER_TOKEN:
      if (transaction.order) {
        return transaction.order.status
      }
      return STATUS.PENDING
    case TRANSACTION_TYPE.SELL_TOKEN:
      if (transaction.sell_token_request) {
        return transaction.sell_token_request.status
      }
      if (transaction.swap_token_transaction) {
        return transaction.swap_token_transaction.status
      }
      return STATUS.PENDING
    case TRANSACTION_TYPE.WITHDRAWAL:
      if (transaction.withdrawal) {
        return transaction.withdrawal.status
      }
      return STATUS.PENDING
    case TRANSACTION_TYPE.REFERRAL_BONUS:
    case TRANSACTION_TYPE.REFERRAL_BONUS_TOKEN:
      if (transaction.notification && transaction.notification.status === STATUS.UNCLAIM) {
        if (transaction.notification.task && transaction.notification.task.status === STATUS.EXPIRED) {
          return STATUS.EXPIRED
        } else {
          return STATUS.UNCLAIM
        }
      }
      if (transaction.payment && transaction.payment.status !== STATUS.PAID) {
        return transaction.payment.status
      }
      return STATUS.RECEIVED
    default:
      return STATUS.RECEIVED
  }
}

function getTransactionStatusColor(transaction) {
  const status = getTransactionStatus(transaction)
  if (status === STATUS.FAILED || status === STATUS.EXPIRED || status === STATUS.REJECTED || status === STATUS.CANCELLED) {
    return '#EF1717'
  } else if (status === STATUS.PAID || status === STATUS.COMPLETED || status === STATUS.RECEIVED || status === STATUS.SWAPPED || status === STATUS.REFUNDED) {
    return '#009567'
  } else if (status === STATUS.APPROVED) {
    if (transaction.type !== TRANSACTION_TYPE.WITHDRAWAL) {
      return '#009567'
    }
  }
  return '#FF6421'
}

function getTransactionStatusBgColor(transaction) {
  const status = getTransactionStatus(transaction)
  if (status === STATUS.FAILED || status === STATUS.EXPIRED || status === STATUS.REJECTED || status === STATUS.CANCELLED) {
    return '#FFD5D5'
  } else if (status === STATUS.PAID || status === STATUS.COMPLETED || status === STATUS.RECEIVED || status === STATUS.SWAPPED || status === STATUS.REFUNDED) {
    return '#E0FFF0'
  } else if (status === STATUS.APPROVED) {
    if (transaction.type !== TRANSACTION_TYPE.WITHDRAWAL) {
      return '#E0FFF0'
    }
  }
  return '#FFF8D5'
}

function getTransactionStatusIcon(transaction) {
  const status = getTransactionStatus(transaction)
  if (status === STATUS.FAILED || status === STATUS.EXPIRED || status === STATUS.REJECTED || status === STATUS.CANCELLED) {
    return 'red'
  } else if (status === STATUS.PAID || status === STATUS.COMPLETED || status === STATUS.RECEIVED || status === STATUS.SWAPPED || status === STATUS.REFUNDED) {
    return 'green'
  } else if (status === STATUS.APPROVED) {
    if (transaction.type !== TRANSACTION_TYPE.WITHDRAWAL) {
      return 'green'
    }
  }
  return 'orange'
}

/**
 * Icon shown instead of the product image for non-product transactions.
 * Returns null when the transaction should display its property image.
 */
function getTransactionIcon(transaction) {
  if (transaction.bond_listing) {
    return require('@/assets/img/bond-listing/bond.svg')
  }
  switch (transaction.type) {
    case TRANSACTION_TYPE.WITHDRAWAL:
      return require('@/assets/img/transaction/withdrawal.svg')
    case TRANSACTION_TYPE.REFERRAL_BONUS:
    case TRANSACTION_TYPE.REFERRAL_BONUS_TOKEN:
    case TRANSACTION_TYPE.REWARD_BONUS:
    case TRANSACTION_TYPE.REWARD_BONUS_TOKEN:
      return require('@/assets/img/transaction/referral_bonus.svg')
    case TRANSACTION_TYPE.ADMIN_ADD_BALANCE:
    case TRANSACTION_TYPE.ADMIN_DEDUCT_BALANCE:
    case TRANSACTION_TYPE.SYSTEM_ADD_BALANCE:
    case TRANSACTION_TYPE.SYSTEM_DEDUCT_BALANCE:
      return require('@/assets/img/transaction/balance_deposited.svg')
    case TRANSACTION_TYPE.DONATION_RENTAL_INCOME:
      return require('@/assets/img/donation/donation.svg')
    default:
      return null
  }
}

/**
 * convertImageToBase64
 *
 * @param {*} imageUrl
 * @returns
 */
async function convertImageToBase64(imageUrl) {
  const proxyUrl = ''
  const fetchUrl = proxyUrl + imageUrl

  try {
    const response = await fetch(fetchUrl)
    const blob = await response.blob()

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(blob)
      reader.onloadend = () => {
        resolve(reader.result)
      };
      reader.onerror = (error) => {
        reject(error)
      }
    })
  } catch (error) {
    console.error('Error converting image to Base64:', error)
    // throw error
  }
}

function toFixedWithoutRounding(number, fixed = 2) {
  var re = new RegExp('^-?\\d+(?:\.\\d{0,' + fixed + '})?');
  return `${number}`.match(re)[0]
}

// Function to shuffle an array
function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]]; // Swap elements
  }
  return array;
}

function payoutDateRules() {
  const locale = i18n.global.locale.value
  if (locale) {
    moment.locale(locale)
  }
  /**
   * Here's the mean of the value to get and show
   *
   * For the [Current Month & Year] rent distribution,
   * which will be paid by [Next 21, Month Year], you will only receive rent distribution
   * for [Total days] ([Date range of Buy Tokens starting from buy date until end of the month])
   * if you do not sell tokens in [Current Month].
   */
  const expectedPayoutDate = process.env.VUE_APP_EXPECTED_PAYOUT_DATE
  const nextPayoutDate = moment().set("date", expectedPayoutDate).add(1, "M").format("DD MMMM YYYY")
  const currentRent = moment().format("MMMM YYYY")

  // Get the receive from. This is the buy token date
  const receiveRentFromDate = moment().startOf('day')
  const receiveRentFrom = receiveRentFromDate.clone().format('DD')
  const receiveRentToEndMonth = receiveRentFromDate.clone().endOf('month')
  const receiveRentTo = receiveRentFromDate.clone().endOf('month').format('DD MMMM YYYY')

  // The month of the buy token date
  const currentMonth = moment().format('MMMM')

  // Total days: Date range of Buy Tokens starting from buy date until end of the month
  const totalDaysReceiveRent = moment(receiveRentToEndMonth).diff(receiveRentFromDate, 'days') + 1 // Include the start day

  // The range of date
  const receiveRentRangeDate = (totalDaysReceiveRent > 1) ? `${receiveRentFrom} - ${receiveRentTo}` : receiveRentTo

  return {
    currentRent,
    nextPayoutDate,
    totalDaysReceiveRent,
    receiveRentRangeDate,
    currentMonth
  }
}

function obfuscateBlockchainInfo(str) {
  if (!str || str.length <= 17) return str;
  const start = str.substring(0, 8);
  const end = str.substring(str.length - 9);
  return `${start}...${end}`;
}

function formatSeconds(seconds) {
  const hrs = Math.floor(seconds / 3600);
  const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const secs = String(seconds % 60).padStart(2, '0');

  // If there are no hours, use MM:SS format
  return hrs > 0 ? `${String(hrs).padStart(2, '0')}:${mins}:${secs}` : `${mins}:${secs}`;
}

// Elements whose text never renders: sanitizeHtml drops them, so they must not be matched or counted.
const NON_RENDERED_TEXT = "script, style, textarea, noscript";

/**
 * Wraps glossary terms in the (HTML) video description with tooltip target spans.
 * Works on the parsed DOM so terms are only matched in text, never inside tags or
 * attribute values. All terms are matched in one pass, longest first; text already
 * inside a `.cls-glossary-text` span (e.g. pasted from another description) is skipped.
 * Callers still sanitize the returned HTML before binding it with v-html.
 * @param {string} description
 * @param {{ key: string, value: string }[]} glossary
 * @param {string} [prefixTooltip]
 * @returns {{ description: string, glossaryTooltips: { id: string, value: string }[] }}
 */
function formattedVideoDescription(description, glossary, prefixTooltip = "glossary-key") {
  const glossaryTooltips = [];
  if (!description || !glossary || !glossary.length) {
    return { description: description || "", glossaryTooltips };
  }

  // One entry per distinct key (case-insensitive, first item wins), keeping the item's index for the id.
  const termsByKey = new Map();
  glossary.forEach((item, idx) => {
    if (!item || !item.key || termsByKey.has(item.key.toLowerCase())) return;
    termsByKey.set(item.key.toLowerCase(), {
      item,
      idx,
      // Ids are tooltip targets, so keep them selector-safe whatever the key contains.
      idKey: item.key.replace(/[^A-Za-z0-9_-]+/g, "-"),
      matchCount: 0,
    });
  });
  if (!termsByKey.size) return { description, glossaryTooltips };

  // A single alternation, longest key first, so one pass over the text finds every term and a
  // longer term ("rental yield") wins over a shorter one starting at the same place ("rental").
  // Word-ish boundaries without lookbehind (unsupported on older Safari): the leading boundary
  // is captured and put back as plain text.
  const alternation = [...termsByKey.values()]
    .map(({ item }) => item.key)
    .sort((a, b) => b.length - a.length)
    .map((key) => key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  const regex = new RegExp(`(^|[^\\w])(${alternation})(?=[^\\w]|$)`, "gi");

  // <template> content is inert: parsing it runs no scripts and loads no images.
  const template = document.createElement("template");
  template.innerHTML = description;

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) {
    // Skip already-wrapped terms, and text the sanitizer drops (no tooltip target would exist).
    if (!walker.currentNode.parentElement?.closest(`.cls-glossary-text, ${NON_RENDERED_TEXT}`)) {
      textNodes.push(walker.currentNode);
    }
  }

  textNodes.forEach((node) => {
    const text = node.nodeValue;
    regex.lastIndex = 0;
    if (!regex.test(text)) return;
    regex.lastIndex = 0;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      const termStart = match.index + match[1].length;
      fragment.appendChild(document.createTextNode(text.slice(lastIndex, termStart)));

      const term = termsByKey.get(match[2].toLowerCase());
      if (!term) {
        // The regex's case folding (uppercase-based) and toLowerCase() disagree on a few
        // characters (final sigma, micro sign): leave such a match as plain text.
        fragment.appendChild(document.createTextNode(match[2]));
        lastIndex = termStart + match[2].length;
        continue;
      }
      term.matchCount++;
      const uniqueId = `${prefixTooltip}-${term.idKey}-${term.idx}-${term.matchCount}`;
      glossaryTooltips.push({ id: uniqueId, value: term.item.value });

      const span = document.createElement("span");
      span.className = "cls-glossary-text";
      span.id = uniqueId;
      span.textContent = match[2];
      fragment.appendChild(span);

      lastIndex = termStart + match[2].length;
    }
    fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    node.replaceWith(fragment);
  });

  return { description: template.innerHTML, glossaryTooltips };
}

// Media in a collapsed preview would turn one line of text into a block of images or players.
const PREVIEW_MEDIA = "img, picture, video, audio, iframe, object, embed, svg, canvas";
// Elements that start a new line when rendered: their text must not run into the next block's.
const LINE_BREAKING = "p, div, li, h1, h2, h3, h4, h5, h6, blockquote, pre, tr, td, th, section, article";

// HTML whitespace only: &nbsp; renders and does not collapse, so it counts as a character.
const COLLAPSIBLE_SPACE = /[ \t\n\r\f]/;

/**
 * Visible text of a parsed fragment, grouped by the text node it comes from, in whole characters
 * (an emoji is never split). Whitespace is collapsed as rendered: a run of it is one space, none
 * before the first word, and a block edge or <br> counts as one space between words. Text that
 * never renders is skipped. A separating space belongs to the node of the word after it, so no
 * segment ends with one.
 * @param {Node} root
 * @returns {{ node: Text, chars: string[] }[]}
 */
function visibleTextSegments(root) {
  const isElement = (node) => node?.nodeType === Node.ELEMENT_NODE;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      isElement(node) && node.matches(NON_RENDERED_TEXT) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
  });
  const segments = [];
  let hasText = false;
  let pendingSpace = false;
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (isElement(node)) {
      if (node.matches(`${LINE_BREAKING}, br`)) pendingSpace = true;
      continue;
    }
    // Text right after a block (`<div>a</div>b`) starts a new line too.
    if (isElement(node.previousSibling) && node.previousSibling.matches(LINE_BREAKING)) pendingSpace = true;
    const chars = [];
    for (const char of node.nodeValue) {
      if (COLLAPSIBLE_SPACE.test(char)) {
        pendingSpace = true;
        continue;
      }
      if (pendingSpace && hasText) chars.push(" ");
      pendingSpace = false;
      hasText = true;
      chars.push(char);
    }
    if (chars.length) segments.push({ node, chars });
  }
  return segments;
}

/**
 * Collapsed preview of HTML: at most `limit` characters of visible text (counted as in
 * visibleTextSegments, so it cuts where truncateText does), cut on the parsed DOM so no tag or
 * entity is split, with everything after the cut removed. Always drops images and embeds.
 * `truncated` is true when anything was left out, text or media, so callers can offer the full
 * description.
 * @param {string} html
 * @param {number} limit
 * @param {string} [suffix]
 * @returns {{ html: string, truncated: boolean }}
 */
function truncateHtml(html, limit, suffix = " ... ") {
  if (!html) return { html: "", truncated: false };

  // <template> content is inert: parsing it runs no scripts and loads no images.
  const template = document.createElement("template");
  template.innerHTML = html;

  const media = template.content.querySelectorAll(PREVIEW_MEDIA);
  media.forEach((el) => el.remove());

  const segments = visibleTextSegments(template.content);
  let remaining = limit;
  let cutNode = null;
  for (let i = 0; i < segments.length; i++) {
    const { node, chars } = segments[i];
    if (chars.length <= remaining) {
      remaining -= chars.length;
      continue;
    }
    const kept = chars.slice(0, remaining).join("").trimEnd();
    const previous = segments[i - 1];
    if (!kept && previous) {
      // Nothing of this node fits (the limit ran out on a word or block boundary): end on the
      // previous node, so the ellipsis never sits alone at the start of the next block.
      previous.node.nodeValue = previous.chars.join("") + suffix;
      cutNode = previous.node;
    } else {
      node.nodeValue = kept + suffix;
      cutNode = node;
    }
    break;
  }

  if (cutNode) {
    // Drop every node after the cut, text or not, keeping open ancestors intact.
    const range = document.createRange();
    range.setStartAfter(cutNode);
    range.setEndAfter(template.content.lastChild);
    range.deleteContents();
  }

  return { html: template.innerHTML, truncated: Boolean(cutNode) || media.length > 0 };
}

/**
 * Plain-text preview of HTML for `{{ }}` bindings: visible text only (see visibleTextSegments),
 * cut at `limit` whole characters.
 * @param {string} html
 * @param {number} limit
 * @param {string} [suffix]
 * @returns {string}
 */
function truncateText(html, limit, suffix = " ...") {
  if (!html) return "";
  const template = document.createElement("template");
  template.innerHTML = html;
  const chars = visibleTextSegments(template.content).flatMap((segment) => segment.chars);
  return chars.length > limit ? chars.slice(0, limit).join("").trimEnd() + suffix : chars.join("");
}

const getImage = (name) => {
  const images = require.context("@/assets/img/", false, /\.svg$/)
  return images("./" + `${name}.svg`)
}

const performAction = (key, cb, delay = 30000) => {
  const lastActionTime = localStorage.getItem(key)
  const now = new Date().getTime()
  if (lastActionTime) {
    if (parseInt(lastActionTime) + delay < now) {
      cb()
      localStorage.setItem(key, `${now}`)
    }
  } else {
    cb()
    localStorage.setItem(key, `${now}`)
  }
}

const clearStorageKeys = () => {
  Object.values(STORAGE_KEYS)
      .filter(item => item.type === "standard")
      .forEach(item => {
        localStorage.removeItem(item.key)
      })
}

const clearStorageActionKeys = () => {
  Object.values(STORAGE_ACTION_KEYS).forEach(key => {
    localStorage.removeItem(key)
  })
}

const getS3DocumentUrl = (path) => {
  if (path) {
    if (path.startsWith('http')) {
      return path
    }
    return base_url + "/" + path;
  }
  return null;
};
const getTnCPPRouteByType = (type) => {
  if (type === DOCUMENT_VERSIONING_TYPES.TERMS_AND_CONDITION) {
    return 'termsAndConditions'
  }
  if (type === DOCUMENT_VERSIONING_TYPES.PRIVACY_POLICY) {
    return 'privacyPolicy'
  }
  return 'home'
}

const convertStringBoolToBool = (value) => {
  if (value === null) return false
  return value.trim().toLowerCase() === "true"
}

const isObjectEmpty = (obj) => {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
}

const getPropertyTokenLeftsPercentage = (total, left) => {
  if (total === 0) {
    return [0, 0];
  }
  if (left === 0) {
    return [100, 0];
  }

  const percentageLeft = left / total;
  let displayPercentage = Math.floor(percentageLeft * 100);

  if (displayPercentage === 0 && left > 0) {
    displayPercentage = 1;
  }
  const percentage = (total - left) / total;
  return [percentage * 100, displayPercentage];
}

const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email;
  const [name, domain] = email.split('@');
  if (!name) return email;
  const maskedName = name.charAt(0) + '*'.repeat(Math.max(0, name.length - 1));
  return `${maskedName}@${domain}`;
}

const truncatedAddress = (address) => {
  if (!address || address.length <= 16) return address;
  return `${address.substring(0, 8)}...${address.substring(address.length - 9)}`;
}

export default helpers;
export {
  notify,
  getBase64,
  urlImage,
  getRandomFloat,
  formattedDuration,
  numberWithCommas,
  imageSizeFromBase64,
  resizeBase64Img,
  getDeviceUUID,
  exchange,
  exchangeNumericValue,
  resolveMetaValueCurrency,
  getErrorMessage,
  formatDate,
  formatNumberIntl,
  formatPercentage,
  userHasPermission,
  isEmail,
  appendTimestampToUrl,
  getCookie,
  getTinyMCEApiKey,
  getTransactionStatus,
  getTransactionTypeKey,
  getTransactionTypeLabel,
  getCreditLabelKeys,
  getSourceReferenceLabelKey,
  getTransactionStatusColor,
  getTransactionStatusBgColor,
  getTransactionStatusIcon,
  getTransactionIcon,
  convertImageToBase64,
  toFixedWithoutRounding,
  shuffleArray,
  payoutDateRules,
  obfuscateBlockchainInfo,
  formatSeconds,
  getImage,
  formattedVideoDescription,
  truncateHtml,
  truncateText,
  performAction,
  clearStorageKeys,
  clearStorageActionKeys,
  getS3DocumentUrl,
  getTnCPPRouteByType,
  convertStringBoolToBool,
  isObjectEmpty,
  getPropertyTokenLeftsPercentage,
  maskEmail,
  truncatedAddress,
};
